import type { QueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';

import {
  queryFilterOptions,
  type FilterDef,
  type FilterOption,
} from '@nucleus-commerce/admin-kit/index-table';
import {
  DraftOrderStatus,
  type Customer,
  type DraftOrder,
} from '@nucleus-commerce/api-client/admin';

import { customerListQueryOptions, customerName, customerQueryOptions } from '@/features/customers';

/**
 * Fields GET /draft-orders sorts on; anything else is a 400. `total` (sort and range filter) reads
 * the total the backend stored at each draft's last write.
 */
export const DRAFT_ORDER_SORTS = ['updatedAt', 'total'] as const;

const valuesOf = (values: Record<string, string>): FilterOption[] =>
  Object.values(values).map((value) => ({ value, label: value }));

/**
 * The filters as the URL and the API spell them, for the route's search schema and loader. The
 * enum `options` drop unknown values the same way the page's filters do, so the prefetch and the
 * page share a query key. Any `currency` marks `total` as whole minor units; the page shows it in
 * the store's currency.
 */
export const DRAFT_ORDER_FILTER_FIELDS = [
  { key: 'status', type: 'enum', options: valuesOf(DraftOrderStatus) },
  { key: 'customerId', type: 'async' },
  { key: 'total', type: 'number-range', currency: 'USD' },
  { key: 'updatedAt', type: 'date-range' },
] as const;

const customerOption = (customer: Customer): FilterOption => ({
  value: customer.id,
  label: customerName(customer) ?? customer.email,
  description: customer.email,
});

export interface DraftOrderFiltersOptions {
  /** The `draftOrders` namespace. */
  t: TFunction;
  queryClient: QueryClient;
  storeId: string;
  currency: string;
}

/** Filters of the draft orders table, in the order the filter menu lists them. */
export function draftOrderFilters({
  t,
  queryClient,
  storeId,
  currency,
}: DraftOrderFiltersOptions): Array<FilterDef<DraftOrder>> {
  return [
    {
      key: 'status',
      label: t('filters.status'),
      type: 'enum',
      pinned: true,
      options: Object.values(DraftOrderStatus).map((value) => ({
        value,
        label: t(`status.${value}`),
      })),
    },
    {
      key: 'customerId',
      label: t('filters.customer'),
      type: 'async',
      // The endpoint takes one customer.
      multiple: false,
      pinned: true,
      aliases: ['customer'],
      ...queryFilterOptions(queryClient, {
        search: (q) => customerListQueryOptions(storeId, { ...(q ? { q } : {}), limit: 20 }),
        toOptions: (page) => page.items.map(customerOption),
        resolve: {
          query: ([id]) => customerQueryOptions(storeId, id ?? ''),
          toOptions: (customer) => [customerOption(customer)],
        },
      }),
    },
    { key: 'total', label: t('filters.total'), type: 'number-range', currency, min: 0 },
    {
      key: 'updatedAt',
      label: t('filters.updatedAt'),
      type: 'date-range',
      aliases: ['updated'],
    },
  ];
}
